import { useCallback, useRef } from 'react';

import { Card, Col, Row, Table } from 'antd';
import { Input } from 'components';
import { useFilters, useWindowSize } from 'hooks';
import { useGetUnifiedRelationsQuery } from 'services';
import { TABLE_STICKY } from 'constant/table';

import FilteredEmptyState from '../../list/filtered-empty-state';

import { ISSUE_FILTER_KEYS } from './issue-status';
import useColumn from './use-column';

const { Search } = Input;

const DEFAULT_FILTERS = { page: 1, itemsPerPage: 20 };
const INTERNAL_KEYS = ['page', 'itemsPerPage', '_sort', '_sortDir'];

/**
 * İlişkili maddeleri listeleyen, sayfalama ve arama destekli tablo.
 * @param {Object} props
 * @param {string} props.baseType - Ana maddenin ansiklopedi tipi
 * @param {string} props.baseId - Ana maddenin orijinal ID'si
 */
function ArticleRelationTable({ baseType, baseId }) {
  const { height } = useWindowSize();
  const debounce = useRef();
  const { filters, setFilters, apiParams } = useFilters(DEFAULT_FILTERS);
  const { columns } = useColumn({ filters });

  const hasActiveFilters = Object.keys(filters).some((k) => !INTERNAL_KEYS.includes(k));

  const resetFilters = useCallback(() => {
    setFilters({ ...DEFAULT_FILTERS });
  }, [setFilters]);

  const { data, isFetching } = useGetUnifiedRelationsQuery(
    { baseType, baseId, ...apiParams },
    { skip: !baseType || !baseId },
  );

  const dataSource = data?.['hydra:member'] ?? [];
  const totalItems = data?.['hydra:totalItems'] ?? 0;

  const pagination = {
    current: filters.page,
    pageSize: filters.itemsPerPage,
    total: totalItems,
    showTotal: (total) => `Toplam İlişkili Madde: ${total}`,
    position: ['bottomRight'],
    showSizeChanger: true,
    pageSizeOptions: [10, 20, 50, 100],
  };

  /** Filtrelenebilir sütun key'leri — onChange'de bunlar temizlenip yeniden set edilir */
  const filterKeys = ['direction', 'relatedType[]', ...ISSUE_FILTER_KEYS, 'isAbandoned', 'isDraft', 'isCancelled'];

  const handleTableChange = (tablePagination, tableFilters, sorter) => {
    const { current, pageSize } = tablePagination;

    // Mevcut sort ve filter key'lerini temizle
    const { _sort: _s, _sortDir: _d, ...rest } = filters;
    const cleaned = Object.fromEntries(
      Object.entries(rest).filter(([key]) => !filterKeys.includes(key)),
    );

    // Sort
    const sortParams = sorter.order
      ? { _sort: sorter.field, _sortDir: sorter.order === 'ascend' ? 'asc' : 'desc' }
      : {};

    // Filter — antd'den gelen tableFilters'ı API formatına dönüştür
    const filterParams = {};
    if (tableFilters.direction?.length) {
      filterParams.direction = tableFilters.direction[0];
    }
    if (tableFilters.relatedType?.length) {
      filterParams['relatedType[]'] = tableFilters.relatedType;
    }
    if (tableFilters.hasIssue?.length) {
      filterParams.hasIssue = tableFilters.hasIssue[0];
    }
    if (tableFilters.isAbandoned?.length) {
      filterParams.isAbandoned = tableFilters.isAbandoned[0];
    }
    if (tableFilters.isDraft?.length) {
      filterParams.isDraft = tableFilters.isDraft[0];
    }
    if (tableFilters.isCancelled?.length) {
      filterParams.isCancelled = tableFilters.isCancelled[0];
    }

    setFilters({
      ...cleaned,
      ...sortParams,
      ...filterParams,
      page: pageSize === filters.itemsPerPage ? current : 1,
      itemsPerPage: pageSize,
    });
  };

  const handleSearch = (text) => {
    clearTimeout(debounce.current);
    debounce.current = setTimeout(() => {
      setFilters({ ...filters, relatedTitle: text, page: 1 });
    }, 300);
  };

  return (
    <div className="gx-mt-5">
      <h3>İlişkili Maddeler</h3>
      <Card>
        <Row justify="space-between" align="middle" className="gx-mb-3">
          <Col xs={14} md={11}>
            <Search
              name="editRelationSearch"
              onSearch={handleSearch}
              defaultValue={filters.relatedTitle}
              placeholder="Madde adına göre arayın..."
              allowClear
              enterButton
            />
          </Col>
        </Row>
        <Table
          dataSource={dataSource}
          columns={columns}
          loading={isFetching}
          pagination={pagination}
          onChange={handleTableChange}
          rowKey="itemId"
          scroll={{ x: 2800, y: height - 500 }}
          sticky={TABLE_STICKY}
          locale={hasActiveFilters ? { emptyText: <FilteredEmptyState onReset={resetFilters} /> } : undefined}
        />
      </Card>
    </div>
  );
}

export default ArticleRelationTable;
